import { activePage } from "../native/pages";
import {
  originalCompact,
  syncOriginalCompact,
  wrapOriginalSection,
} from "./native-original-controls";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import { documentPoint, type Camera } from "../native/geometry";
import { createRegion, type SelectionRegion } from "../native/region";
import { applyRegion, traceRegion } from "../render/native-region";
import type { ImageStore } from "../render/image-store";
import type { EngineDocument, Point } from "../scene/model";
import type { WorkTool } from "./native-tool-groups";
export class NativeRegions {
  copied?: (document: EngineDocument, ids: readonly string[]) => void;
  region: SelectionRegion | null = null;
  tool: SelectionRegion["kind"] | null = null;
  busy = false;
  private gesture: {
    id: number;
    start: Point;
    points: Point[];
    before: SelectionRegion | null;
    feather: number;
    beforeFeather: number;
  } | null = null;
  private pendingFeather = 0;
  private generation = -1;
  private token = 0;
  private fragment: Awaited<ReturnType<typeof applyRegion>>["fragment"] | null =
    null;
  private panel = document.getElementById("marquee-properties")!;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private overlay: HTMLCanvasElement,
    private camera: Camera,
    private images: ImageStore,
    private changed: () => void,
    private select: () => void,
    private blocked: () => boolean,
    private message: (text: string, error?: boolean) => void,
  ) {
    for (const [id, action] of [
      ["marquee-copy-layer", "copy"],
      ["marquee-cut-layer", "cut"],
      ["marquee-delete", "delete"],
    ] as const)
      document.getElementById(id)!.onclick = () => void this.action(action);
    const feather = document.getElementById(
      "marquee-feather",
    ) as HTMLInputElement;
    feather.oninput = () => {
      const value = Number(feather.value);
      if (this.region) this.region = { ...this.region, feather: value };
      else this.pendingFeather = value;
      if (this.gesture) this.gesture.feather = value;
      document.getElementById("marquee-feather-value")!.textContent =
        feather.value + " px";
      this.changed();
    };
    document.getElementById("marquee-feather-value")!.onblur = (e) => {
      const v = Number(
        (e.target as HTMLElement).textContent?.replace(/[^\d.]/g, ""),
      );
      feather.value = String(Math.max(0, Math.min(100, v || 0)));
      feather.dispatchEvent(new Event("input"));
    };
    wrapOriginalSection("marquee-properties", [
      {
        title: "Выделенная область",
        icon: "enclosed",
        selectors: [
          "#marquee-target-status",
          ".marquee-feather-control",
          ".marquee-action-buttons",
        ],
        open: true,
      },
    ]);
    originalCompact("native-region-compact", [
      { id: "marquee-feather", icon: "blur", title: "Растушёвка" },
      { id: "marquee-cut-layer", icon: "cut", title: "Вырезать на новый слой" },
      {
        id: "marquee-copy-layer",
        icon: "copy",
        title: "Скопировать на новый слой",
      },
      { id: "marquee-delete", icon: "delete", title: "Удалить" },
    ]);
  }
  activate(tool: WorkTool | null) {
    const mode = tool?.endsWith("-area")
      ? (tool.replace("-area", "") as SelectionRegion["kind"])
      : null;
    this.cancelGesture();
    this.tool = mode;
    if (mode && !this.region) this.pendingFeather = 0;
    this.panel.hidden = !mode;
    this.changed();
  }
  get active() {
    return !!this.gesture;
  }
  cancelGesture() {
    if (this.gesture) {
      this.region = this.gesture.before;
      this.pendingFeather = this.gesture.beforeFeather;
      this.gesture = null;
      this.changed();
    }
  }
  event(e: PointerEvent) {
    if (!this.tool || this.blocked() || this.busy) return false;
    if (
      this.pointer.space ||
      e.button === 1 ||
      ((e.ctrlKey || e.metaKey) && !this.gesture)
    )
      return false;
    const p = documentPoint({ x: e.clientX, y: e.clientY }, this.camera);
    if (e.type === "pointerdown" && e.button === 0) {
      this.overlay.focus();
      this.gesture = {
        id: e.pointerId,
        start: p,
        points: [p],
        before: this.region,
        // Existing regions keep their own feather; a replacement starts fresh.
        feather: this.region ? 0 : this.pendingFeather,
        beforeFeather: this.pendingFeather,
      };
      this.pendingFeather = 0;
      this.overlay.setPointerCapture(e.pointerId);
    }
    const g = this.gesture;
    if (!g || g.id !== e.pointerId) return false;
    if (e.type === "pointercancel") {
      this.cancelGesture();
      return true;
    }
    if (e.type === "pointermove" || e.type === "pointerup") {
      if (
        Math.hypot(p.x - g.points.at(-1)!.x, p.y - g.points.at(-1)!.y) >
        1 / this.camera.zoom
      )
        g.points.push(p);
      this.region = createRegion(this.tool, g.start, p, g.points, g.feather);
    }
    if (e.type === "pointerup") {
      this.gesture = null;
      if (this.overlay.hasPointerCapture(e.pointerId))
        this.overlay.releasePointerCapture(e.pointerId);
      if (
        !this.region ||
        this.region.box.width < 1 ||
        this.region.box.height < 1
      ) {
        this.region = g.before;
        this.pendingFeather = g.beforeFeather;
      }
    }
    this.changed();
    return true;
  }
  key(e: KeyboardEvent) {
    if (
      this.blocked() ||
      (e.target as HTMLElement).closest(
        "input,textarea,select,[contenteditable=true]",
      )
    )
      return false;
    if (e.key === "Escape" && (this.tool || this.busy)) {
      this.token++;
      this.busy = false;
      if (this.gesture) this.cancelGesture();
      else {
        this.region = null;
        this.pendingFeather = 0;
        this.select();
      }
      this.changed();
      return true;
    }
    if (
      this.region &&
      (e.ctrlKey || e.metaKey) &&
      ["KeyC", "KeyX"].includes(e.code)
    ) {
      void this.action(e.code === "KeyX" ? "cut" : "clipboard");
      return true;
    }
    if (this.region && this.tool && e.key === "Delete") {
      void this.action("delete");
      return true;
    }
    return false;
  }
  async pasteFragment() {
    if (!this.fragment || this.blocked() || this.busy) return false;
    const { node, asset } = structuredClone(this.fragment);
    node.id = crypto.randomUUID();
    this.session.document = {
      ...this.session.document,
      assets: [...this.session.document.assets, asset],
    };
    this.session.replaceNodes([...this.session.page.nodes, node]);
    this.session.select([node.id]);
    this.session.finish();
    this.select();
    return true;
  }
  private async action(action: "copy" | "cut" | "delete" | "clipboard") {
    if (
      this.busy ||
      this.blocked() ||
      !this.region ||
      this.session.selectedNodes.length !== 1
    )
      return;
    const token = ++this.token,
      revision = this.session.revision,
      generation = this.session.generation,
      source = this.session.document,
      region = structuredClone(this.region),
      id = this.session.selectedId!;
    this.busy = true;
    this.changed();
    try {
      const result = await applyRegion(
        source,
        id,
        region,
        action,
        this.images,
        () =>
          this.token === token &&
          this.session.revision === revision &&
          this.session.generation === generation &&
          !this.blocked(),
      );
      if (action === "clipboard") {
        this.fragment = result.fragment;
        this.copied?.(
          {
            ...source,
            assets: [result.fragment.asset],
            pages: [{ ...activePage(source), nodes: [result.fragment.node] }],
          },
          [result.fragment.node.id],
        );
        this.message("Фрагмент скопирован.");
      } else {
        this.session.document = result.document;
        this.session.select(
          action === "delete" ? [id] : [result.fragment.node.id],
        );
        this.session.finish();
        this.select();
        this.region = null;
        this.pendingFeather = 0;
        this.message("Область обработана.");
      }
    } catch (error) {
      this.message((error as Error).message, true);
    } finally {
      if (token === this.token) this.busy = false;
      this.changed();
    }
  }
  render(ctx: CanvasRenderingContext2D) {
    if (this.generation !== this.session.generation) {
      this.generation = this.session.generation;
      this.region = null;
      this.pendingFeather = 0;
      this.token++;
      this.busy = false;
    }
    this.panel.hidden = !this.tool;
    document.getElementById("native-region-compact")!.hidden = !this.tool;
    for (const b of document.querySelectorAll<HTMLButtonElement>(
      ".marquee-action-buttons button,[data-region-action]",
    ))
      b.disabled =
        this.busy || !this.region || this.session.selectedNodes.length !== 1;
    const feather =
      this.gesture?.feather ?? this.region?.feather ?? this.pendingFeather;
    (document.getElementById("marquee-feather") as HTMLInputElement).value =
      String(feather);
    const featherValue = document.getElementById("marquee-feather-value")!;
    if (document.activeElement !== featherValue)
      featherValue.textContent = feather + " px";
    syncOriginalCompact("native-region-compact");
    const title =
      this.tool === "circle"
        ? "Круглая область"
        : this.tool === "lasso"
          ? "Произвольная область"
          : "Прямоугольная область";
    document.getElementById("marquee-properties-title")!.textContent = title;
    document.getElementById("marquee-target-status")!.textContent =
      this.session.selectedNodes.length === 1
        ? String(this.session.selected!.layer.name)
        : "Сначала выберите один слой, затем выделите область на холсте.";
    if (this.tool && this.region) {
      ctx.save();
      ctx.translate(this.camera.x, this.camera.y);
      ctx.scale(this.camera.zoom, this.camera.zoom);
      ctx.lineWidth = 1 / this.camera.zoom;
      traceRegion(ctx, this.region);
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      ctx.strokeStyle = "#222222";
      ctx.setLineDash([4 / this.camera.zoom, 4 / this.camera.zoom]);
      ctx.stroke();
      ctx.restore();
    }
  }
}
